import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { queryStringToObject, validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { useLocation, useHistory } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import Button from '../../components/Button';
import FormGroupWithError from '../../components/FormGroupWithError';
import SearchOrganisation from '../../components/SearchOrganisation';
import GlobalModal from '../../components/GlobalModal';
import LoginCreateModal from '../Login/LoginCreateModal';
import OrganisationLogo from '../../components/OrganisationLogo';

// ICONS
import { ReactComponent as ArrowLeft } from '../../assets/svg/arrow-left.svg';

// TYPES
import {
	DomainProfileType,
	OrganisationSearchOrganisationSearchSummary,
	OrganisationFindOrganisationSummary
} from '../../api/models';

interface FindOrganisationTypes {}

export interface FormTypes {
	organisationIdentifier?: string;
	profileType?: DomainProfileType;
}

export const FindOrganisation: React.FC<FindOrganisationTypes> = ({}) => {
	const location = useLocation();
	const history = useHistory();
	const lists = useAppSelector((state) => state?.lists);
	const isLoggedIn = useAppSelector(({ user }) => user.loggedIn);

	const [showError, setShowError] = React.useState<boolean>(false);

	const [formErrorMessage, setFormErrorMessage] = React.useState<string>();

	const [modalIsOpen, setModalIsOpen] = React.useState<boolean>(false);
	const [returnUrl, setReturnUrl] = React.useState<string>('');
	const [organisationIdentifier, setOrganisationIdentifier] = React.useState<string | undefined>('');
	const [profileType, setProfileType] = React.useState<DomainProfileType>();
	const [organisationName, setOrganisationName] = React.useState<string | undefined>('');

	const [organisation, setOrganisation] = React.useState<OrganisationFindOrganisationSummary | undefined>();

	const locationParams = location.state;
	const queryParams = queryStringToObject();
	const params = { ...locationParams, ...queryParams };

	const getOrganisationDetail = async () => {
		if (params.id !== undefined) {
			const response = await apibridge.getOrganisationByIdentifier(params.id);

			if (response.isError) {
				setShowError(true);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else if (response.result) {
				setShowError(false);

				setOrganisation({
					...response.result
				});
			}
		}
	};

	React.useEffect(() => {
		getOrganisationDetail();
	}, []);

	const goBack = () => {
		history.goBack();
	};

	const doContinue = (values: FormTypes) => {
		const url = '/join-organisation';
		// const query = `?id=${values.organisationIdentifier}&type=${values.profileType}`;

		if (isLoggedIn) {
			history.push({
				pathname: url,
				state: {
					organisationIdentifier: values.organisationIdentifier,
					profileType: values.profileType
				}
			});
		} else {
			setReturnUrl(url);

			setOrganisationIdentifier(values.organisationIdentifier);

			setProfileType(values.profileType);

			setModalIsOpen(true);
		}
	};

	const checkLocationParams = (params: any) => {
		const url = '/join-organisation';

		if (!isLoggedIn) {
			setReturnUrl(url);

			setModalIsOpen(true);

			setOrganisationIdentifier(params.id);

			setProfileType(params.type);
		} else {
			history.push({
				pathname: url,
				state: {
					organisationIdentifier: params.id,
					profileType: params.type
				}
			});
		}
	};

	React.useEffect(() => {
		const locationParams = location.state;
		const queryParams = queryStringToObject();
		const params = { ...locationParams, ...queryParams };

		if (params.id !== undefined && params.type !== undefined) {
			checkLocationParams(params);
		} else if (params.id !== undefined) {
			setOrganisationIdentifier(params.id);
		} else {
			setOrganisationIdentifier(params.id);

			setOrganisationName(params.name);
		}
	}, []);

	const initialValues: FormTypes = {
		organisationIdentifier: organisationIdentifier || '',
		profileType: lists?.profileTypes
			? (lists.profileTypes.filter((item) => item.value !== DomainProfileType.SuperAdministrator)[0]
					.value as DomainProfileType)
			: undefined
	};

	const validationSchema = Yup.object({
		organisationIdentifier: Yup.string().required('Required'),
		profileType: Yup.string().required('Required')
	});

	// Display Role Text
	const displayRoleText = (value: string, label: string) => {
		if (value === DomainProfileType.Staff) {
			return (
				<span className="role-label">
					Staff or Volunteer
					<p className="role-info">
						Any person 18+ years of age, with an official role at the organisation, paid or unpaid.
					</p>
				</span>
			);
		} else if (value === DomainProfileType.Parent) {
			return (
				<span className="role-label">
					Parent or Parent Volunteer
					<p className="role-info">Includes carers or guardians.</p>
				</span>
			);
		} else if (value === DomainProfileType.Student) {
			return (
				<span className="role-label">
					Child or Student
					<p className="role-info">
						13yrs+ years of age, including children or youth working, volunteering or studying.
					</p>
				</span>
			);
		} else if (value === DomainProfileType.Visitor) {
			return (
				<span className="role-label">
					Visitor
					<p className="role-info">Any other adult visiting the organisation</p>
				</span>
			);
		} else {
			return <span className="role-label">{label}</span>;
		}
	};

	return (
		<div className="page-find-organisation">
			<Button className="btn-text-icon go-back" onClick={goBack} buttonIcon={<ArrowLeft className="link-icon" />}>
				Back
			</Button>

			<div className="section-header">
				{organisationName || organisation?.name ? (
					<>
						{organisation?.image ? <OrganisationLogo src={organisation.image} /> : null}

						<h1 className="h1">Join {organisationName || organisation?.name}</h1>
					</>
				) : (
					<h1 className="h1">Find an organisation to join</h1>
				)}
			</div>

			<div className="section-content">
				<Formik
					enableReinitialize={true}
					initialValues={initialValues}
					validationSchema={validationSchema}
					onSubmit={doContinue}
				>
					{(formik) => {
						return (
							<Form translate="yes">
								<div className="content">
									{params.id || organisationName ? null : (
										<FormGroupWithError name="organisationIdentifier">
											<label htmlFor="organisationIdentifier">Search for an organisation</label>

											<SearchOrganisation
												placeholder="Search..."
												isFullWidth={true}
												onChange={(item: OrganisationSearchOrganisationSearchSummary) => {
													formik.setFieldValue('organisationIdentifier', item ? item.identifier : '');
												}}
											/>
										</FormGroupWithError>
									)}

									<FormGroupWithError name="profileType">
										<label htmlFor="profileType">What are you joining as?</label>

										{lists.profileTypes &&
											lists.profileTypes
												.filter((item) => item.value !== DomainProfileType.SuperAdministrator)
												.map((item) => (
													<label className="form-control-radio role-selection-screen" key={item.value}>
														<Field
															name="profileType"
															type="radio"
															value={item.value}
															checked={formik.values.profileType === item.value}
														/>
														<span>{displayRoleText(item.value || '', item.label || '')}</span>
													</label>
												))}
									</FormGroupWithError>
								</div>

								<div className="actions">
									<Button type="submit" className="btn-primary full-width">
										Continue
									</Button>
								</div>
							</Form>
						);
					}}
				</Formik>
			</div>

			<GlobalModal className="w-auto" useDefaultStructure={false} isOpen={modalIsOpen} setIsOpen={setModalIsOpen}>
				<LoginCreateModal
					returnUrl={returnUrl}
					organisationIdentifier={organisationIdentifier}
					profileType={profileType}
				/>
			</GlobalModal>
		</div>
	);
};

export default FindOrganisation;
